import { useLoaderData, useNavigate, useSearchParams } from "@remix-run/react";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import type {
  Category,
  CategoryCalculatedProperties,
} from "../../../../types/Category";
import { Filter } from "~/components/Shop/Filter";
import { OrderBy } from "~/components/Shop/OrderBy";
import type { Brand } from "../../../../types/Brand";
import { LargeFilter } from "~/components/Shop/LargeFilter";
import { FilteredBy } from "~/components/Shop/FilteredBy";
import { useShopSearchParams } from "~/hooks/useShopSearchParams";
import { getAllCategories } from "~/category.server";
import { getBrands, getProducts, sendSearchToQueue } from "./queries";
import {
  filterByBrand,
  filterByCategory,
  filterByQuery,
  filterBySubCategory,
} from "./filters";
import { orderByCreatedAt, orderByPopularity, orderByPrice } from "./orderBy";
import { FullScreenBorder } from "~/components/frontpage/FullScreenBorder";
import { H1 } from "~/components/H1";
import { ProductInfoKV } from "../../../../types/KV/ProductInfoSchema";
import { SmallProduct } from "~/components/SmallProduct";
import { Inventory } from "../../../../types/Inventory";

export const meta: MetaFunction = () => {
  return [
    { title: "Nola - Beauty And Bullsh*t Don't Mix" },
    {
      name: "theme-color",
      content: "#EFD4D7",
    },
  ];
};

export const loader = async (args: LoaderFunctionArgs) => {
  const isMobile = /Mobile|Android|iP(hone|ad|od)/i.test(
    args.request.headers.get("User-Agent") || "",
  );

  const products = await getProducts(
    args.context.cloudflare.env.PRODUCT_INFO as KVNamespace,
  );


  const allCategories = await getAllCategories(args.context.cloudflare.env.DB);
  const brands = await getBrands(args.context.cloudflare.env.DB);
  const url = new URL(args.request.url);
  const query = url.searchParams.get("query");

  const brand = url.searchParams.getAll("brand");
  const categories = url.searchParams.getAll("category");
  const subCategories = url.searchParams.getAll("subcategory");
  const orderBy = url.searchParams.get("orderBy") || "popularity";

  let filteredProducts = products?.filter((p) => p.hidden !== true);
  if (filteredProducts) {
    filteredProducts = filterByQuery(filteredProducts, query);
    filteredProducts = filterByBrand(filteredProducts, brand);
    filteredProducts = filterByCategory(filteredProducts, categories);
    filteredProducts = filterBySubCategory(filteredProducts, subCategories);
  }

  //get inventory for all products and update soldOut status
  if (filteredProducts !== undefined) {
    filteredProducts = await Promise.all(
      filteredProducts.map(async (product) => {
        const res = await args.context.cloudflare.env.INVENTORY.get<Inventory>(
          product.id,
          "json",
        );
        if (res === null || product.hasVariations) {
          return product;
        }
        return {
          ...product,
          soldOut: res.quantity <= 0,
        };
      })
    );
  }

  if (orderBy === "priceLow" && filteredProducts) {
    filteredProducts = orderByPrice(filteredProducts, "lowToHigh");
  } else if (orderBy === "priceHigh" && filteredProducts) {
    filteredProducts = orderByPrice(filteredProducts, "highToLow");
  } else if (orderBy === "newest" && filteredProducts) {
    filteredProducts = orderByCreatedAt(filteredProducts);
  } else if (orderBy === "popularity" && filteredProducts) {
    filteredProducts = orderByPopularity(filteredProducts);
  }

  if (query) {
    await sendSearchToQueue({
      searchTerm: query,
      resultCount: filteredProducts?.length || 0,
      url: url.toString(),
    }, args.context.cloudflare.env.PRODUCT_SEARCH);
  }

  return json({
    categories: allCategories,
    products: filteredProducts,
    brands,
    orderBy,
    isMobile,
  });
};

export default function Index() {
  const {
    categories,
    products,
    brands,
    orderBy,
    isMobile,
  }: {
    products: Array<ProductInfoKV>;
    categories: Array<
      Category &
      CategoryCalculatedProperties & {
        children: Array<Category & CategoryCalculatedProperties>;
      }
    >;
    brands: Array<Brand>;
    orderBy: string;
    isMobile: boolean;
  } = useLoaderData();

  const { selected } = useShopSearchParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") || "";

  const activeCategories = categories.filter((c) =>
    selected.categories.some((s) => s === c.slug),
  );
  const activeSubCategories = categories.filter((c) =>
    selected.subcategories.some((s) => s === c.slug),
  );
  const activeBrands = brands.filter((b) =>
    selected.brands.some((s) => s === b.slug),
  );

  return (
    <div className="">
      <div className="block h-20 bg-[#EFD4D7] xs:hidden"></div>
      <div className="hidden h-[440px] flex-col items-center justify-center bg-[#EFD4D7] text-center font-FKScreamer xs:flex">
        <H1>
          Beauty and <br /> BULLSH*T DON’t MIX
        </H1>
      </div>
      <div className="hidden xs:block">
        <FullScreenBorder
          text="Cruelty-free, for both animals and people"
          type="green"
        />
      </div>

      <div className="my-6 flex w-full flex-row items-center justify-between border-b-[1px] px-4 lg:px-14">
        <input
          key={query}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              navigate(`/shop?query=${e.currentTarget.value}`);
            }
          }}
          defaultValue={query}
          placeholder="Leita..."
          className="block h-10 rounded-lg bg-transparent text-lg font-semibold uppercase text-[#0E0F0F] focus:bg-white focus:placeholder-[#0E0F0F] focus:outline-none"
        ></input>
        <div
          className="cursor-pointer text-base font-normal text-black"
          onClick={(e) => {
            setSearchParams((sp) => {
              sp.delete("query");
              return sp;
            });
          }}
        >
          Hreinsa
        </div>
      </div>

      <div className="mx-8 flex flex-col">
        <div className="flex min-h-16 flex-row flex-wrap items-center justify-between border-b-0.5">
          <div className="order-1 mt-4 block lg:hidden">
            <Filter
              categories={categories}
              brands={brands}
              availableCount={products.length}
            />
          </div>

          <div className="order-3 lg:order-2 lg:ml-[300px]">
            <FilteredBy
              filteredBy={[
                ...(activeBrands?.map((activeBrand) => {
                  return {
                    label: activeBrand.name,
                    slug: activeBrand.slug,
                    key: "brand",
                  };
                }) || []),
                ...(activeCategories?.map((activeCategory) => {
                  return {
                    label: activeCategory.name,
                    slug: activeCategory.slug,
                    key: "category",
                  };
                }) || []),
                ...(activeSubCategories?.map((activeSubCategory) => {
                  return {
                    label: activeSubCategory.name,
                    slug: activeSubCategory.slug,
                    key: "subcategory",
                  };
                }) || []),
              ]}
            />
          </div>
          <div className="order-2 mt-4 lg:order-3 lg:mt-0">
            <OrderBy
              onClick={(orderBy) => {
                setSearchParams(
                  (sp) => {
                    sp.set("orderBy", orderBy);
                    return sp;
                  },
                  { preventScrollReset: true },
                );
              }}
              label={
                orderBy === "newest"
                  ? "Nýjast"
                  : orderBy === "priceLow"
                    ? "Ódýrast"
                    : orderBy === "popularity"
                      ? "Vinsælast"
                      : "Dýrast"
              }
              options={[
                {
                  label: "Nýjast",
                  value: "newest",
                  active: orderBy === "newest",
                },
                {
                  label: "Ódýrast",
                  value: "priceLow",
                  active: orderBy === "priceLow",
                },
                {
                  label: "Dýrast",
                  value: "priceHigh",
                  active: orderBy === "priceHigh",
                },
                {
                  label: "Vinsælast",
                  value: "popularity",
                  active: orderBy === "popularity",
                },
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr]">
          <div className="invisible hidden w-[300px] flex-grow lg:visible lg:inline-block">
            <LargeFilter categories={categories} brands={brands} />
          </div>

          {products.length === 0 ? (
            <div className="mt-8 flex w-full flex-1 flex-col items-center justify-start text-center text-lg font-semibold">
              Engar vörur fundust með þessum leitarskilyrðum:{" "}
              {searchParams.get("query")}
              <div
                className="mb-4 mt-4 cursor-pointer"
                onClick={() => setSearchParams({})}
              >
                Hreinsa leit
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-10 mt-8 grid gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-10 grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {products.map((p) => {
                  return (
                    <SmallProduct
                      key={p.id}
                      url={p.imageUrl || ""}
                      brand={p.brand?.name || ""}
                      title={p.name}
                      slug={p.slug}
                      price={p.priceFrom}
                      soldOut={p.soldOut}
                      hasVariations={p.hasVariations}
                      discount={Math.max(
                        p.discount || 0,
                        p.brand?.discount || 0,
                      )}
                      isMobile={isMobile}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
